(() => {
  'use strict';

  const doc = document;
  const root = doc.documentElement;
  window.theme = window.theme || window.PremiumTheme || {};
  const SELECTORS = {
    drawer: '[data-cart-drawer]',
    menu: '[data-menu-drawer]',
    search: '[data-search-dialog]'
  };

  const q = (selector, scope = doc) => scope.querySelector(selector);
  const qa = (selector, scope = doc) => [...scope.querySelectorAll(selector)];
  const route = (name, fallback) => window.theme?.routes?.[name] || fallback;
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let lastFocusedElement = null;
  let predictiveController = null;
  let predictiveTimer = null;
  let cartRequest = null;
  let facetsTrigger = null;
  const pendingAnimationItems = new Set();
  let animationFallbackTicking = false;

  function setBusy(element, busy) {
    if (!element) return;
    element.toggleAttribute('aria-busy', busy);
    element.classList.toggle('is-loading', busy);
    if ('disabled' in element) element.disabled = busy;
  }

  function setPageLocked(locked) {
    root.classList.toggle('is-locked', locked);
    doc.body.classList.toggle('is-locked', locked);
  }

  function closeFacets(panel, restoreFocus = true) {
    if (!panel) return;
    panel.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    panel.removeAttribute('aria-modal');
    panel.removeAttribute('role');
    setFacetsIsolation(panel, false);
    q('[data-facets-open]', panel.closest('[data-facets]') || doc)?.setAttribute('aria-expanded', 'false');
    setPageLocked(false);
    if (restoreFocus && facetsTrigger instanceof HTMLElement) facetsTrigger.focus({ preventScroll: true });
    facetsTrigger = null;
  }

  function setFacetsIsolation(panel, isolated) {
    if (!panel) return;
    if (!isolated) {
      qa('[data-facets-inerted]').forEach((element) => {
        element.removeAttribute('inert');
        element.removeAttribute('data-facets-inerted');
      });
      return;
    }
    let branch = panel;
    while (branch.parentElement && branch !== doc.body) {
      [...branch.parentElement.children].forEach((sibling) => {
        if (sibling === branch || sibling.hasAttribute('inert')) return;
        sibling.setAttribute('inert', '');
        sibling.setAttribute('data-facets-inerted', '');
      });
      branch = branch.parentElement;
    }
  }

  function syncFacetsAccessibility(scope = doc) {
    const mobile = window.matchMedia('(max-width: 989px)').matches;
    qa('[data-facets-panel]', scope).forEach((panel) => {
      if (mobile) {
        panel.setAttribute('aria-hidden', panel.classList.contains('is-open') ? 'false' : 'true');
        return;
      }
      const wasOpen = panel.classList.contains('is-open');
      panel.classList.remove('is-open');
      panel.removeAttribute('aria-hidden');
      panel.removeAttribute('aria-modal');
      panel.removeAttribute('role');
      q('[data-facets-open]', panel.closest('[data-facets]') || doc)?.setAttribute('aria-expanded', 'false');
      if (wasOpen) {
        setFacetsIsolation(panel, false);
        setPageLocked(false);
      }
    });
  }

  function getFocusable(container) {
    if (!container) return [];
    return qa(
      'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      container
    ).filter((element) => !element.hidden && element.offsetParent !== null);
  }

  function trapFocus(event, container) {
    if (event.key !== 'Tab') return;
    const focusable = getFocusable(container);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && doc.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && doc.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function activateLayer(element, trigger) {
    if (!element) return;
    lastFocusedElement = trigger || doc.activeElement;
    if (trigger?.hasAttribute?.('aria-expanded')) trigger.setAttribute('aria-expanded', 'true');
    element.hidden = false;
    if (element instanceof HTMLDialogElement && !element.open) {
      try { element.showModal(); } catch (_error) { element.setAttribute('open', ''); }
    }
    element.setAttribute('aria-hidden', 'false');
    element.classList.add('is-open');
    setPageLocked(true);
    requestAnimationFrame(() => getFocusable(element)[0]?.focus({ preventScroll: true }));
  }

  function deactivateLayer(element, restoreFocus = true) {
    if (!element) return;
    element.classList.remove('is-open');
    element.setAttribute('aria-hidden', 'true');
    if (element.id) {
      qa(`[aria-controls="${element.id}"]`).forEach((trigger) => trigger.setAttribute('aria-expanded', 'false'));
    }
    const finish = () => {
      if (!element.classList.contains('is-open')) {
        if (element instanceof HTMLDialogElement && element.open) element.close();
        element.hidden = true;
      }
    };
    element.addEventListener('transitionend', finish, { once: true });
    window.setTimeout(finish, 450);
    if (!q('.is-open[data-cart-drawer], .is-open[data-menu-drawer], .is-open[data-search-dialog]')) {
      setPageLocked(false);
    }
    if (restoreFocus && lastFocusedElement instanceof HTMLElement) lastFocusedElement.focus({ preventScroll: true });
  }

  function closeAllLayers(except = null) {
    qa(`${SELECTORS.drawer}.is-open, ${SELECTORS.menu}.is-open, ${SELECTORS.search}.is-open`).forEach((layer) => {
      if (layer !== except) deactivateLayer(layer, false);
    });
  }

  function formatMoney(cents) {
    const currency = window.theme?.currency || 'USD';
    try {
      return new Intl.NumberFormat(root.lang || 'es', { style: 'currency', currency }).format(Number(cents) / 100);
    } catch (_error) {
      return `${(Number(cents) / 100).toFixed(2)} ${currency}`;
    }
  }

  function announce(message) {
    const liveRegion = q('[data-live-region]');
    if (!liveRegion) return;
    liveRegion.textContent = '';
    window.setTimeout(() => { liveRegion.textContent = message; }, 50);
  }

  function initHeader(scope = doc) {
    qa('[data-header]', scope).forEach((header) => {
      if (header.dataset.ready === 'true') return;
      header.dataset.ready = 'true';
      const update = () => header.classList.toggle('is-scrolled', window.scrollY > 12);
      update();
      window.addEventListener('scroll', update, { passive: true });
    });
  }

  function initAnnouncement(scope = doc) {
    qa('[data-announcement-slider]', scope).forEach((slider) => {
      if (slider.dataset.ready === 'true') return;
      slider.dataset.ready = 'true';
      const slides = qa('[data-announcement-item], [data-announcement-slide]', slider);
      if (slides.length < 2) return;
      let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      const show = (nextIndex) => {
        slides[index].classList.remove('is-active');
        slides[index].setAttribute('aria-hidden', 'true');
        index = (nextIndex + slides.length) % slides.length;
        slides[index].classList.add('is-active');
        slides[index].setAttribute('aria-hidden', 'false');
      };
      q('[data-announcement-prev], [data-announcement-previous]', slider)?.addEventListener('click', () => show(index - 1));
      q('[data-announcement-next]', slider)?.addEventListener('click', () => show(index + 1));
      if ((slider.dataset.autoplay === 'true' || slider.dataset.cycle === 'true') && !reducedMotion()) {
        const rawInterval = Number(slider.dataset.interval || 5);
        const interval = Math.max(2500, rawInterval > 100 ? rawInterval : rawInterval * 1000);
        let timer = window.setInterval(() => show(index + 1), interval);
        slider.addEventListener('mouseenter', () => window.clearInterval(timer));
        slider.addEventListener('mouseleave', () => { timer = window.setInterval(() => show(index + 1), interval); });
        slider.addEventListener('focusin', () => window.clearInterval(timer));
        slider.addEventListener('focusout', () => { timer = window.setInterval(() => show(index + 1), interval); });
      }
    });
  }

  function revealAnimationItem(item) {
    if (!item) return;
    item.dataset.animated = 'true';
    item.classList.add('is-visible');
    pendingAnimationItems.delete(item);
  }

  function revealPendingAnimations() {
    animationFallbackTicking = false;
    pendingAnimationItems.forEach((item) => {
      if (!item.isConnected) {
        pendingAnimationItems.delete(item);
        return;
      }
      const rect = item.getBoundingClientRect();
      if (rect.top <= window.innerHeight * 0.96 && rect.bottom >= 0) revealAnimationItem(item);
    });
  }

  function requestAnimationFallback() {
    if (animationFallbackTicking || !pendingAnimationItems.size) return;
    animationFallbackTicking = true;
    requestAnimationFrame(revealPendingAnimations);
  }

  function initAnimations(scope = doc) {
    const items = qa("[data-animate], [data-animation]:not([data-animation='none']) [data-animate-item]", scope)
      .filter((item) => item.dataset.animated !== 'true');
    if (!items.length) return;
    root.classList.add('animations-ready');
    if (reducedMotion() || !('IntersectionObserver' in window)) {
      items.forEach(revealAnimationItem);
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        revealAnimationItem(entry.target);
        observer.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    items.forEach((item) => {
      pendingAnimationItems.add(item);
      observer.observe(item);
    });

    // Shopify's editor can initialize sections while its preview iframe is still
    // settling. A double frame preserves the entrance transition and guarantees
    // that already-visible blocks are revealed even if the observer misses them.
    requestAnimationFrame(() => requestAnimationFrame(requestAnimationFallback));
    window.setTimeout(requestAnimationFallback, 900);
  }

  async function getCart() {
    const fallback = `${route('root', '/')}${String(route('root', '/')).endsWith('/') ? '' : '/'}cart.js`;
    const response = await fetch(route('cartJson', fallback), { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('No se pudo consultar el carrito.');
    return response.json();
  }

  function updateCartCount(cart) {
    qa('[data-cart-count]').forEach((badge) => {
      badge.textContent = cart.item_count;
      badge.hidden = cart.item_count === 0;
      badge.classList.remove('is-updating');
      void badge.offsetWidth;
      badge.classList.add('is-updating');
    });
  }

  async function refreshCart(openAfter = false) {
    if (cartRequest) cartRequest.abort();
    cartRequest = new AbortController();
    const signal = cartRequest.signal;
    const drawerUrl = `${route('cart', '/cart')}?section_id=cart-drawer`;
    try {
      const currentBeforeRefresh = q(SELECTORS.drawer);
      const wasOpen = Boolean(currentBeforeRefresh?.open || currentBeforeRefresh?.classList.contains('is-open'));
      const [cart, sectionResponse] = await Promise.all([
        getCart(),
        fetch(drawerUrl, { signal, headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      ]);
      updateCartCount(cart);
      if (sectionResponse.ok) {
        const html = await sectionResponse.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const freshDrawer = q(SELECTORS.drawer, parsed);
        const currentDrawer = q(SELECTORS.drawer);
        if (freshDrawer && currentDrawer) currentDrawer.replaceWith(freshDrawer);
      }
      if (openAfter || wasOpen) {
        const drawer = q(SELECTORS.drawer);
        closeAllLayers(drawer);
        activateLayer(drawer, openAfter ? doc.activeElement : null);
      }
      doc.dispatchEvent(new CustomEvent('theme:cart-updated', { detail: { cart } }));
      return cart;
    } finally {
      cartRequest = null;
    }
  }

  async function addToCart(form, submitter) {
    const errorBox = q('[data-product-form-error], [data-product-form-message]', form)
      || q('[data-product-form-error], [data-product-form-message]', form.closest('[data-product-root], [data-featured-product]') || form.parentElement);
    if (errorBox) {
      errorBox.hidden = true;
      errorBox.textContent = '';
    }
    setBusy(submitter, true);
    try {
      const response = await fetch(route('cartAdd', '/cart/add.js'), {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: new FormData(form)
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || payload.message || 'No se pudo agregar el producto.');
      if (window.theme?.settings?.cartType === 'page') {
        window.location.assign(route('cart', '/cart'));
        return;
      }
      await refreshCart(true);
      announce(window.theme?.strings?.addedToCart || 'Producto agregado al carrito.');
      form.dispatchEvent(new CustomEvent('theme:product-added', { bubbles: true, detail: payload }));
    } catch (error) {
      if (errorBox) {
        errorBox.textContent = error.message;
        errorBox.hidden = false;
      }
      announce(error.message);
    } finally {
      setBusy(submitter, false);
    }
  }

  async function changeCartLine(lineElement, quantity, control) {
    const key = lineElement?.dataset.lineKey || lineElement?.dataset.key;
    const line = Number(lineElement?.dataset.line || 0);
    if (!key && !line) return;
    const quantityInput = lineElement ? q('input[type="number"]', lineElement) : null;
    const previousQuantity = Number(quantityInput?.defaultValue || quantityInput?.value || 0);
    const inlineError = lineElement ? q('[data-line-error], [data-cart-line-error]', lineElement) : null;
    const drawerError = q('[data-cart-error]', lineElement?.closest?.('[data-cart-drawer]') || doc);
    [inlineError, drawerError].forEach((errorBox) => {
      if (!errorBox) return;
      errorBox.hidden = true;
      errorBox.textContent = '';
    });
    setBusy(control, true);
    try {
      const response = await fetch(route('cartChange', '/cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify(key ? { id: key, quantity } : { line, quantity })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.description || 'No se pudo actualizar el carrito.');
      await refreshCart(false);
      if (!q(`${SELECTORS.drawer}.is-open`) && q('[data-cart-form]')) window.location.reload();
      announce(window.theme?.strings?.cartUpdated || 'Carrito actualizado.');
    } catch (error) {
      if (quantityInput) quantityInput.value = String(previousQuantity);
      [inlineError, drawerError].forEach((errorBox) => {
        if (!errorBox) return;
        errorBox.textContent = error.message;
        errorBox.hidden = false;
      });
      announce(error.message);
    } finally {
      setBusy(control, false);
    }
  }

  function parseProductVariants(container) {
    const script = q('[data-product-variants]', container);
    if (!script) return [];
    try { return JSON.parse(script.textContent); } catch (_error) { return []; }
  }

  function getSelectedVariant(container, changedSelect) {
    const variants = parseProductVariants(container);
    if (!variants.length) return null;
    if (changedSelect?.matches('[data-variant-select]') && /^\d+$/.test(changedSelect.value)) {
      const direct = variants.find((variant) => String(variant.id) === changedSelect.value);
      if (direct) return direct;
    }
    const positioned = qa('[data-option-position]', container);
    const positions = [...new Set(positioned.map((selector) => Number(selector.dataset.optionPosition)))].sort();
    const values = positions.length
      ? positions.map((position) => {
          const controls = positioned.filter((selector) => Number(selector.dataset.optionPosition) === position);
          const selected = controls.find((selector) => selector.matches('select') || selector.checked);
          return selected?.value;
        })
      : qa('[data-option-selector], select[name^="options"]', container).map((selector) => selector.value);
    return variants.find((variant) => variant.options.every((option, index) => option === values[index])) || null;
  }

  function updateProductVariant(container, variant) {
    const forms = qa('form[action*="/cart/add"]', container);
    forms.forEach((form) => {
      const input = q('[name="id"]', form);
      if (input && variant) input.value = variant.id;
      qa('[type="submit"]', form).forEach((button) => {
        button.disabled = !variant?.available;
        const label = q('[data-add-to-cart-text]', button) || q('[data-add-to-cart-text]', form);
        if (label) label.textContent = variant?.available
          ? (label.dataset.availableLabel || window.theme?.strings?.addToCart || 'Agregar al carrito')
          : (label.dataset.soldOutLabel || window.theme?.strings?.soldOut || 'Agotado');
      });
    });
    qa('[data-sticky-add]', container).forEach((button) => {
      button.disabled = !variant?.available;
      button.textContent = variant?.available
        ? (button.dataset.availableLabel || window.theme?.strings?.addToCart || 'Agregar al carrito')
        : (button.dataset.soldOutLabel || window.theme?.strings?.soldOut || 'Agotado');
    });
    qa('[data-product-price]', container).forEach((price) => {
      if (!variant) return;
      const priceRoot = price.matches('[data-price]') ? price : q('[data-price]', price) || price;
      qa('[data-price-current], [data-price-regular]', priceRoot).forEach((current) => {
        current.textContent = formatMoney(variant.price);
      });
      const onSale = Number(variant.compare_at_price) > Number(variant.price);
      const sale = q('.price__sale', priceRoot);
      const displaySale = onSale && Boolean(sale);
      priceRoot.classList.toggle('price--on-sale', displaySale);
      if (sale) sale.hidden = !displaySale;
      const compare = q('[data-price-compare]', priceRoot);
      if (compare) {
        compare.hidden = !onSale;
        if (onSale) compare.textContent = formatMoney(variant.compare_at_price);
      }
      const saving = q('.price__saving', priceRoot);
      if (saving) {
        saving.hidden = !onSale;
        if (onSale) saving.textContent = `${window.theme?.strings?.save || 'Ahorra'} ${formatMoney(variant.compare_at_price - variant.price)}`;
      }
      const badge = q('[data-price-badge]', priceRoot);
      if (badge) {
        badge.hidden = !displaySale;
        if (displaySale) badge.textContent = `-${Math.round((variant.compare_at_price - variant.price) * 100 / variant.compare_at_price)}%`;
      }
      qa('[data-unit-price]', priceRoot).forEach((unitPrice) => {
        const measurement = variant.unit_price_measurement;
        const hasUnitPrice = variant.unit_price != null && Boolean(measurement);
        unitPrice.hidden = !hasUnitPrice;
        if (!hasUnitPrice) return;
        const amount = q('[data-unit-price-amount]', unitPrice);
        const referenceValue = q('[data-unit-price-reference-value]', unitPrice);
        const referenceUnit = q('[data-unit-price-reference-unit]', unitPrice);
        if (amount) amount.textContent = formatMoney(variant.unit_price);
        if (referenceValue) {
          const value = Number(measurement.reference_value) || 1;
          referenceValue.textContent = String(value);
          referenceValue.hidden = value === 1;
        }
        if (referenceUnit) referenceUnit.textContent = measurement.reference_unit || '';
      });
    });
    qa('[data-sticky-price]', container).forEach((price) => {
      if (variant) price.textContent = formatMoney(variant.price);
    });
    qa('[data-sticky-variant-title]', container).forEach((title) => {
      title.textContent = variant?.title && variant.title !== 'Default Title' ? variant.title : '';
    });
    qa('[data-variant-sku]', container).forEach((skuLine) => {
      const hasSku = Boolean(variant?.sku);
      const value = q('span', skuLine) || skuLine;
      if (hasSku) value.textContent = variant.sku;
      skuLine.classList.toggle('is-hidden', !hasSku);
    });
    const quantityRule = variant?.quantity_rule;
    if (quantityRule) {
      qa('[data-quantity-input]', container).forEach((input) => {
        const minimum = Math.max(1, Number(quantityRule.min) || 1);
        const increment = Math.max(1, Number(quantityRule.increment) || 1);
        const maximum = quantityRule.max == null ? Infinity : Number(quantityRule.max);
        const maximumAligned = Number.isFinite(maximum)
          ? minimum + Math.floor(Math.max(0, maximum - minimum) / increment) * increment
          : Infinity;
        input.min = String(minimum);
        input.step = String(increment);
        if (Number.isFinite(maximum)) input.max = String(maximum);
        else input.removeAttribute('max');
        const current = Number(input.value);
        let next = Number.isFinite(current) ? Math.max(minimum, current) : minimum;
        next = minimum + Math.ceil((next - minimum) / increment) * increment;
        if (Number.isFinite(maximumAligned)) next = Math.min(maximumAligned, next);
        input.value = String(next);
      });
    }
    qa('[data-product-stock]', container).forEach((stock) => {
      const threshold = Number(stock.dataset.lowStockThreshold || 0);
      const inventory = Number(variant?.inventory_quantity);
      const lowStock = Boolean(variant?.available && threshold > 0 && Number.isFinite(inventory) && inventory > 0 && inventory <= threshold);
      stock.classList.toggle('is-unavailable', !variant?.available);
      stock.classList.toggle('product-stock--out', !variant?.available);
      stock.classList.toggle('product-stock--low', lowStock);
      const stockText = q('[data-stock-text]', stock) || stock;
      if (!variant?.available) {
        stockText.textContent = stock.dataset.outOfStockText || window.theme?.strings?.soldOut || 'Agotado';
      } else if (lowStock) {
        stockText.textContent = (stock.dataset.lowStockText || 'Solo quedan [quantity]')
          .replace('[quantity]', String(inventory));
      } else {
        stockText.textContent = stock.dataset.inStockText || window.theme?.strings?.inStock || 'Disponible';
      }
    });
    qa('[data-selected-option]', container).forEach((label, index) => {
      if (variant?.options?.[index]) label.textContent = variant.options[index];
    });
    const mediaId = variant?.featured_media?.id;
    if (mediaId) activateGalleryMedia(container, String(mediaId));
    if (variant && container.dataset.updateUrl !== 'false' && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    }
    container.dispatchEvent(new CustomEvent('theme:variant-changed', { bubbles: true, detail: { variant } }));
  }

  function activateGalleryMedia(scope, mediaId, scrollActive = true) {
    const gallery = q('[data-product-gallery]', scope) || (scope.matches?.('[data-product-gallery]') ? scope : null);
    if (!gallery) return;
    const stackedLayout = Boolean(gallery.closest('.product-layout--stacked'));
    const mediaItems = qa('[data-gallery-media]', gallery);
    mediaItems.forEach((media) => {
      const active = String(media.dataset.mediaId) === String(mediaId);
      if (!active && media.classList.contains('is-active')) {
        qa('video', media).forEach((video) => video.pause());
        qa('iframe', media).forEach((frame) => {
          const source = frame.getAttribute('src');
          if (source) frame.setAttribute('src', source);
        });
        qa('model-viewer', media).forEach((model) => model.pause?.());
      }
      media.classList.toggle('is-active', active);
      media.setAttribute('aria-hidden', stackedLayout || active ? 'false' : 'true');
      media.toggleAttribute('inert', !stackedLayout && !active);
      if (active && scrollActive) media.scrollIntoView({ behavior: reducedMotion() ? 'auto' : 'smooth', block: 'nearest', inline: 'nearest' });
    });
    qa('[data-gallery-thumbnail]', gallery).forEach((thumbnail) => {
      const active = String(thumbnail.dataset.mediaId || thumbnail.dataset.targetMedia) === String(mediaId);
      thumbnail.classList.toggle('is-active', active);
      thumbnail.setAttribute('aria-current', active ? 'true' : 'false');
    });
    const activeIndex = mediaItems.findIndex((media) => String(media.dataset.mediaId) === String(mediaId));
    const counter = q('[data-gallery-current]', gallery);
    if (counter && activeIndex >= 0) counter.textContent = String(activeIndex + 1);
  }

  function initProductGalleries(scope = doc) {
    qa('[data-product-gallery]', scope).forEach((gallery) => {
      const viewport = q('[data-gallery-viewport]', gallery);
      if (!viewport || viewport.dataset.scrollReady === 'true') return;
      viewport.dataset.scrollReady = 'true';
      let scrollTimer = null;
      const syncScrolledSlide = () => {
        if (viewport.scrollWidth <= viewport.clientWidth + 2) return;
        const viewportRect = viewport.getBoundingClientRect();
        const center = viewportRect.left + viewportRect.width / 2;
        const slides = qa('[data-gallery-media]', gallery);
        const closest = slides.reduce((best, slide) => {
          const rect = slide.getBoundingClientRect();
          const distance = Math.abs(rect.left + rect.width / 2 - center);
          return !best || distance < best.distance ? { slide, distance } : best;
        }, null)?.slide;
        if (closest?.dataset.mediaId && !closest.classList.contains('is-active')) {
          activateGalleryMedia(gallery, closest.dataset.mediaId, false);
        }
      };
      viewport.addEventListener('scroll', () => {
        window.clearTimeout(scrollTimer);
        scrollTimer = window.setTimeout(syncScrolledSlide, 90);
      }, { passive: true });
    });
  }

  function initProductRecommendations(scope = doc) {
    qa('product-recommendations', scope).forEach(async (element) => {
      if (element.dataset.loaded === 'true' || !element.dataset.url) return;
      element.dataset.loaded = 'true';
      try {
        const response = await fetch(element.dataset.url);
        if (!response.ok) return;
        const html = await response.text();
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const incoming = q('product-recommendations', parsed) || parsed.body;
        if (incoming?.innerHTML.trim()) {
          element.innerHTML = incoming.innerHTML;
          initAnimations(element);
          initSliders(element);
        } else {
          element.hidden = true;
        }
      } catch (_error) {
        element.hidden = true;
      }
    });
  }

  function initStickyAddToCart(scope = doc) {
    qa('[data-sticky-atc]', scope).forEach((sticky) => {
      if (sticky.dataset.ready === 'true') return;
      sticky.dataset.ready = 'true';
      sticky.hidden = false;
      sticky.setAttribute('aria-hidden', 'true');
      sticky.setAttribute('inert', '');
      const section = sticky.closest('[data-product-root], .main-product');
      const mainButton = q('[data-product-form] [type="submit"], form[action*="/cart/add"] [type="submit"]', section || doc);
      if (!mainButton || !('IntersectionObserver' in window)) return;
      const observer = new IntersectionObserver(([entry]) => {
        const visible = !entry.isIntersecting && window.scrollY > mainButton.getBoundingClientRect().top;
        sticky.classList.toggle('is-visible', visible);
        sticky.setAttribute('aria-hidden', visible ? 'false' : 'true');
        sticky.toggleAttribute('inert', !visible);
      }, { threshold: 0 });
      observer.observe(mainButton);
    });
  }

  function initSliders(scope = doc) {
    qa('[data-slider]', scope).forEach((slider) => {
      if (slider.dataset.ready === 'true') return;
      slider.dataset.ready = 'true';
      const track = q('[data-slider-track]', slider) || slider;
      const move = (direction) => {
        const item = q('[data-slider-item]', track) || track.firstElementChild;
        const amount = item ? item.getBoundingClientRect().width + 20 : track.clientWidth * 0.85;
        track.scrollBy({ left: direction * amount, behavior: reducedMotion() ? 'auto' : 'smooth' });
      };
      q('[data-slider-prev]', slider)?.addEventListener('click', () => move(-1));
      q('[data-slider-next]', slider)?.addEventListener('click', () => move(1));
    });
  }

  function initPredictiveSearch(scope = doc) {
    qa('[data-predictive-search-input]', scope).forEach((input) => {
      if (input.dataset.ready === 'true') return;
      input.dataset.ready = 'true';
      input.addEventListener('input', () => {
        window.clearTimeout(predictiveTimer);
        const term = input.value.trim();
        const wrapper = input.closest('[data-predictive-search]') || input.closest('form') || input.parentElement;
        const results = q('[data-predictive-search-results]', wrapper) || q('[data-predictive-search-results]');
        if (!results) return;
        if (window.theme?.settings?.predictiveSearch === false || wrapper?.dataset.enabled === 'false') return;
        if (term.length < 2) {
          results.innerHTML = '';
          results.hidden = true;
          return;
        }
        results.hidden = false;
        results.setAttribute('aria-busy', 'true');
        predictiveTimer = window.setTimeout(async () => {
          predictiveController?.abort();
          predictiveController = new AbortController();
          try {
            const limit = Number(wrapper?.dataset.resultsLimit || 8);
            const url = `${route('predictiveSearch', '/search/suggest')}?q=${encodeURIComponent(term)}&resources[type]=query,product,collection,page,article&resources[limit]=${limit}&section_id=predictive-search`;
            const response = await fetch(url, { signal: predictiveController.signal });
            if (!response.ok) throw new Error();
            const html = await response.text();
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            const incoming = q('[data-predictive-search-results]', parsed);
            results.innerHTML = incoming ? incoming.innerHTML : html;
            results.setAttribute('aria-busy', 'false');
          } catch (error) {
            if (error.name !== 'AbortError') {
              results.innerHTML = `<p class="predictive-search__empty">${window.theme?.strings?.searchError || 'No fue posible cargar las sugerencias.'}</p>`;
              results.setAttribute('aria-busy', 'false');
            }
          }
        }, 250);
      });
    });
  }

  function initVideo(scope = doc) {
    qa('[data-video-play]', scope).forEach((button) => {
      if (button.dataset.ready === 'true') return;
      button.dataset.ready = 'true';
      button.addEventListener('click', () => {
        const wrapper = button.closest('[data-video-wrapper]') || button.parentElement;
        const media = q('video, iframe, [data-video-embed]', wrapper);
        if (!media) return;
        if (media.dataset.src && !media.getAttribute('src')) media.setAttribute('src', media.dataset.src);
        wrapper.classList.add('is-playing');
        button.hidden = true;
        if (media.play) media.play().catch(() => {});
      });
    });
  }

  function init(scope = doc) {
    root.classList.remove('no-js');
    root.classList.add('js');
    initHeader(scope);
    initAnnouncement(scope);
    initAnimations(scope);
    initProductRecommendations(scope);
    initProductGalleries(scope);
    initStickyAddToCart(scope);
    initSliders(scope);
    initPredictiveSearch(scope);
    initVideo(scope);
    syncFacetsAccessibility(scope);
  }

  doc.addEventListener('click', (event) => {
    const trigger = event.target.closest('button, a, [role="button"], [data-menu-close], [data-cart-close], [data-search-close]');
    if (!trigger) return;

    if (trigger.matches('[data-menu-open]')) {
      event.preventDefault();
      const menu = q(SELECTORS.menu);
      closeAllLayers(menu);
      activateLayer(menu, trigger);
      trigger.setAttribute('aria-expanded', 'true');
      return;
    }
    if (trigger.matches('[data-menu-close], [data-menu-overlay]')) {
      event.preventDefault();
      deactivateLayer(trigger.closest(SELECTORS.menu) || q(SELECTORS.menu));
      q('[data-menu-open]')?.setAttribute('aria-expanded', 'false');
      return;
    }
    if (trigger.matches('[data-search-open]')) {
      event.preventDefault();
      const search = q(SELECTORS.search);
      closeAllLayers(search);
      activateLayer(search, trigger);
      window.setTimeout(() => q('input[type="search"]', search)?.focus(), 50);
      return;
    }
    if (trigger.matches('[data-search-close], [data-search-overlay]')) {
      event.preventDefault();
      deactivateLayer(trigger.closest(SELECTORS.search) || q(SELECTORS.search));
      return;
    }
    if (trigger.matches('[data-cart-open]')) {
      event.preventDefault();
      const drawer = q(SELECTORS.drawer);
      closeAllLayers(drawer);
      activateLayer(drawer, trigger);
      return;
    }
    if (trigger.matches('[data-cart-close], [data-cart-overlay]')) {
      event.preventDefault();
      deactivateLayer(trigger.closest(SELECTORS.drawer) || q(SELECTORS.drawer));
      return;
    }
    if (trigger.matches('[data-quantity-button], [data-quantity-minus], [data-quantity-plus]')) {
      event.preventDefault();
      const wrapper = trigger.closest('[data-quantity-selector], .quantity-selector, .quantity');
      const input = q('input[type="number"]', wrapper);
      if (!input) return;
      const direction = trigger.matches('[data-quantity-minus]') || trigger.dataset.quantityButton === 'minus' || trigger.dataset.action === 'decrease' ? -1 : 1;
      const min = Number(input.min || 1);
      const max = Number(input.max || Infinity);
      const step = Math.max(1, Number(input.step) || 1);
      input.value = Math.min(max, Math.max(min, Number(input.value || min) + direction * step));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    if (trigger.matches('[data-cart-quantity]')) {
      event.preventDefault();
      const line = trigger.closest('[data-cart-line]');
      const input = q('input[type="number"]', line);
      if (!input) return;
      const delta = trigger.dataset.cartQuantity === 'minus' || trigger.dataset.action === 'decrease' ? -1 : 1;
      const step = Math.max(1, Number(input.step) || 1);
      const next = Math.max(0, Number(input.value || 0) + delta * step);
      input.value = next;
      changeCartLine(line, next, trigger);
      return;
    }
    if (trigger.matches('[data-cart-quantity-change]')) {
      event.preventDefault();
      const line = trigger.closest('[data-cart-line], [data-cart-item]');
      const input = q('input[type="number"]', line);
      if (!input) return;
      const delta = trigger.dataset.cartQuantityChange === 'decrease' ? -1 : 1;
      const step = Math.max(1, Number(input.step) || 1);
      const next = Math.max(0, Number(input.value || 0) + delta * step);
      input.value = next;
      changeCartLine(line, next, trigger);
      return;
    }
    if (trigger.matches('[data-line-remove], [data-cart-remove]')) {
      event.preventDefault();
      changeCartLine(trigger.closest('[data-cart-line], [data-cart-item]'), 0, trigger);
      return;
    }
    if (trigger.matches('[data-gallery-thumbnail]')) {
      event.preventDefault();
      activateGalleryMedia(trigger.closest('[data-product-root], [data-product-gallery]') || doc, trigger.dataset.mediaId || trigger.dataset.targetMedia);
      return;
    }
    if (trigger.matches('[data-gallery-previous], [data-gallery-next]')) {
      event.preventDefault();
      const gallery = trigger.closest('[data-product-gallery]');
      const slides = qa('[data-gallery-media]', gallery);
      const activeIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
      const direction = trigger.matches('[data-gallery-previous]') ? -1 : 1;
      const next = (activeIndex + direction + slides.length) % slides.length;
      const mediaId = slides[next]?.dataset.mediaId;
      if (mediaId) activateGalleryMedia(gallery, mediaId);
      const current = q('[data-gallery-current]', gallery);
      if (current) current.textContent = String(next + 1);
      return;
    }
    if (trigger.matches('[data-product-zoom]')) {
      event.preventDefault();
      const targetId = trigger.getAttribute('aria-controls') || trigger.dataset.target;
      const modal = targetId ? doc.getElementById(targetId) : q('[data-zoom-dialog]', trigger.closest('[data-product-root]'));
      if (modal?.showModal) modal.showModal();
      else if (modal) activateLayer(modal, trigger);
      else if (trigger.dataset.zoomSrc) {
        const dialog = doc.createElement('dialog');
        dialog.className = 'zoom-dialog';
        dialog.setAttribute('aria-label', trigger.getAttribute('aria-label') || 'Vista ampliada del producto');
        dialog.innerHTML = `<button type="button" class="zoom-dialog__close" data-zoom-close aria-label="${window.theme?.strings?.close || 'Cerrar'}">×</button><img src="${trigger.dataset.zoomSrc}" alt="">`;
        doc.body.append(dialog);
        dialog.addEventListener('close', () => dialog.remove(), { once: true });
        dialog.addEventListener('click', (clickEvent) => { if (clickEvent.target === dialog) dialog.close(); });
        dialog.showModal();
      }
      return;
    }
    if (trigger.matches('[data-zoom-close]')) {
      const modal = trigger.closest('dialog, [data-zoom-dialog]');
      if (modal?.close) modal.close();
      else deactivateLayer(modal);
      return;
    }
    if (trigger.matches('[data-sticky-atc-button], [data-sticky-add]')) {
      event.preventDefault();
      const section = trigger.closest('[data-product-root], .main-product') || doc;
      q('[data-product-form] [type="submit"], form[action*="/cart/add"] [type="submit"]', section)?.click();
      return;
    }
    if (trigger.matches('[data-facets-open]')) {
      event.preventDefault();
      const panel = q('[data-facets-panel]', trigger.closest('[data-facets]') || doc);
      panel?.classList.add('is-open');
      panel?.setAttribute('role', 'dialog');
      panel?.setAttribute('aria-modal', 'true');
      panel?.setAttribute('aria-hidden', 'false');
      setFacetsIsolation(panel, true);
      facetsTrigger = trigger;
      trigger.setAttribute('aria-expanded', 'true');
      setPageLocked(true);
      requestAnimationFrame(() => getFocusable(panel)[0]?.focus({ preventScroll: true }));
      return;
    }
    if (trigger.matches('[data-facets-close]')) {
      event.preventDefault();
      const panel = trigger.closest('[data-facets-panel]');
      closeFacets(panel);
    }
  });

  doc.addEventListener('submit', (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-product-form], [data-quick-add], form[action*="/cart/add"]')) return;
    const submitter = event.submitter || q('[type="submit"]', form);
    if (submitter?.name === 'checkout' || submitter?.closest('.shopify-payment-button') || form.dataset.ajax === 'false') return;
    event.preventDefault();
    addToCart(form, submitter);
  });

  doc.addEventListener('change', (event) => {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;
    if (target.matches('[data-variant-select], [data-option-selector], [data-option-position], select[name^="options"], input[name^="options"]')) {
      const container = target.closest('[data-product-root], [data-featured-product]');
      if (container) updateProductVariant(container, getSelectedVariant(container, target));
    }
    if (target.matches('[data-cart-line] input[type="number"], input[data-cart-quantity-input]')) {
      changeCartLine(target.closest('[data-cart-line], [data-cart-item]'), Math.max(0, Number(target.value || 0)), target);
    }
    if (target.matches('[data-sort-select]')) {
      const form = target.closest('form');
      if (form) form.requestSubmit();
      else {
        const url = new URL(window.location.href);
        url.searchParams.set('sort_by', target.value);
        window.location.assign(url.toString());
      }
    }
  });

  doc.addEventListener('keydown', (event) => {
    const predictiveInput = event.target.closest?.('[data-predictive-search-input]');
    if (predictiveInput && event.key === 'ArrowDown') {
      const wrapper = predictiveInput.closest('[data-predictive-search]') || predictiveInput.closest('form');
      const firstResult = q('[data-predictive-search-results] a, [data-predictive-search-results] button', wrapper || doc);
      if (firstResult) {
        event.preventDefault();
        firstResult.focus();
        return;
      }
    }
    const resultControl = event.target.closest?.('[data-predictive-search-results] a, [data-predictive-search-results] button');
    if (resultControl && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      const controls = getFocusable(resultControl.closest('[data-predictive-search-results]'));
      const index = controls.indexOf(resultControl);
      if (index >= 0 && controls.length) {
        event.preventDefault();
        controls[(index + (event.key === 'ArrowDown' ? 1 : -1) + controls.length) % controls.length].focus();
        return;
      }
    }
    const openFacets = q('[data-facets-panel].is-open');
    if (openFacets) {
      if (event.key === 'Escape') {
        event.preventDefault();
        closeFacets(openFacets);
      } else {
        trapFocus(event, openFacets);
      }
      return;
    }
    const activeLayer = q(`${SELECTORS.drawer}.is-open, ${SELECTORS.menu}.is-open, ${SELECTORS.search}.is-open`);
    if (!activeLayer) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      deactivateLayer(activeLayer);
      q('[data-menu-open]')?.setAttribute('aria-expanded', 'false');
    } else {
      trapFocus(event, activeLayer);
    }
  });

  window.addEventListener('scroll', requestAnimationFallback, { passive: true });
  window.addEventListener('resize', () => {
    requestAnimationFallback();
    syncFacetsAccessibility();
  }, { passive: true });

  doc.addEventListener('shopify:block:select', (event) => {
    const selectedItem = event.target.matches?.('[data-animate], [data-animate-item]')
      ? event.target
      : event.target.closest?.('[data-animate], [data-animate-item]');
    if (selectedItem) revealAnimationItem(selectedItem);
  });
  doc.addEventListener('shopify:section:select', (event) => {
    qa('[data-animate], [data-animate-item]', event.target).forEach(revealAnimationItem);
  });
  doc.addEventListener('shopify:section:load', (event) => init(event.target));
  doc.addEventListener('shopify:section:reorder', () => {
    initAnimations();
    requestAnimationFallback();
  });
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', () => init());
  else init();
})();
